/**
 * User preferences
 */

import {
  EDITOR_MODE_HTML,
  POST_TYPE_LINK,
  DARK_MODE_LIGHT
} from '@/lib/constants'

// Be sure to update ALLOWED_PREFERENCES in backend/apps/users/constants.py
const PREFERENCES = {
  DARK_MODE: 'postpone:darkMode',
  EDITOR_MODE: 'postpone:editorMode',
  POST_TYPE: 'postpone:postType',
  SHOW_SURVEY: 'postpone:showFeedbackSurveyV2',
  SHOW_SOCIAL_MEDIA_SURVEY: 'postpone:showSocialMediaSurvey',
  SHOW_ANNUAL_UPSELL_BANNER: 'postpone:showAnnualUpsellBanner',
  SHOW_UPGRADE_BANNER: 'postpone:sub',
  SHOW_TIMEZONE_BANNER: 'postpone:tzb',
  SHOW_TWITTER_BANNER: 'postpone:tweet',
  SHOW_SUBREDDIT_SETTINGS: 'postpone:sst',
  DASHBOARD_DISPLAY: 'postpone:dashboardDisplay',
  PREVIEW_IMAGES_KEY: 'postpone:previewImages',
  DEFAULT_PER_PAGE: 'postpone:defaultPerPage',
  LAST_READ_CHANGELOG: 'postpone:lastReadChangelog',
  LAST_USED_INBOX_USERNAME: 'postpone:lastUsedInboxUsername',
  LAST_USED_CALENDAR_VIEW: 'postpone:lastUsedCalendarView',
  SHOW_DASHBOARD_CHECKLIST: 'postpone:showDashboardChecklist',
  PLATFORM_DISPLAY: 'postpone:platformDisplay',
  REPOST_SETTINGS: 'postpone:repostSettings'
}

class FakeLocalStorage {
  constructor() {
    this._store = {}
  }

  getItem(key) {
    return this._store[key]
  }

  setItem(key, value) {
    this._store[key] = value
  }
}

class VuexStorage {
  constructor(store) {
    this._store = store
  }

  getItem(key) {
    if (
      !this._store.state.profile ||
      !this._store.state.profile.preferences ||
      Object.keys(this._store.state.profile.preferences).length === 0
    ) {
      return null
    }
    return this._store.state.profile.preferences[key]
  }

  setItem(key, value) {
    if (
      this._store.state.profile.preferences &&
      this._store.state.profile.preferences[key] === value
    ) {
      // Avoid unnecessary API calls.
      return
    }
    this._store.dispatch('setPreference', { key, value })
  }
}

export class UserPrefs {
  constructor(store, params) {
    // Despite not really being used, these fields are still required to exist
    // on a UserPrefs instance in order for Vue's reactivity system to track
    // them and react to changes.
    Object.values(PREFERENCES).forEach((key) => {
      this[key] = null
    })

    this.storage = process.browser
      ? new VuexStorage(store)
      : new FakeLocalStorage()
  }

  /**
   * Getters/setters for different data types.
   */
  getBoolean(key, defaultValue) {
    let storageValue = this.storage.getItem(key)
    this[key] = storageValue ? storageValue === '1' : defaultValue
    return this[key]
  }

  setBoolean(key, value) {
    const stringValue = !!value ? '1' : '0'
    this.storage.setItem(key, stringValue)
    this[key] = stringValue
  }

  getString(key, defaultValue = '') {
    let storageValue = this.storage.getItem(key)
    this[key] = storageValue ? storageValue : defaultValue
    return this[key]
  }

  setString(key, value) {
    this.storage.setItem(key, value.toString())
    this[key] = value
  }

  getNumber(key, defaultValue) {
    let storageValue = this.storage.getItem(key)
    this[key] = storageValue ? parseInt(storageValue) : defaultValue
    return this[key]
  }

  setNumber(key, value) {
    this.storage.setItem(key, value.toString())
    this[key] = value
  }

  getDate(key, defaultValue) {
    let storageValue = this.storage.getItem(key)
    this[key] = storageValue ? new Date(storageValue) : defaultValue
    return this[key]
  }

  setDate(key, value) {
    this.storage.setItem(key, value.toISOString().split('T')[0])
    this[key] = value
  }

  getObject(key, defaultValue) {
    let storageValue = this.storage.getItem(key)
    this[key] = storageValue ? JSON.parse(storageValue) : defaultValue
    return this[key]
  }

  setObject(key, value) {
    this.storage.setItem(key, JSON.stringify(value))
    this[key] = value
  }

  /**
   * Fields
   */
  get darkMode() {
    return this.getString(PREFERENCES.DARK_MODE, DARK_MODE_LIGHT)
  }

  set darkMode(darkMode) {
    this.setString(PREFERENCES.DARK_MODE, darkMode)
  }

  get editorMode() {
    return this.getString(PREFERENCES.EDITOR_MODE, EDITOR_MODE_HTML)
  }

  set editorMode(editorMode) {
    this.setString(PREFERENCES.EDITOR_MODE, editorMode)
  }

  get dashboardDisplay() {
    return this.getString(PREFERENCES.DASHBOARD_DISPLAY, 'list')
  }

  set dashboardDisplay(dashboardDisplay) {
    this.setString(PREFERENCES.DASHBOARD_DISPLAY, dashboardDisplay)
  }

  get platformDisplay() {
    return this.getString(PREFERENCES.PLATFORM_DISPLAY, 'reddit')
  }

  set platformDisplay(platformDisplay) {
    this.setString(PREFERENCES.PLATFORM_DISPLAY, platformDisplay)
  }

  get postType() {
    return this.getString(PREFERENCES.POST_TYPE, POST_TYPE_LINK)
  }

  set postType(postType) {
    this.setString(PREFERENCES.POST_TYPE, postType)
  }

  get showSurvey() {
    return this.getBoolean(PREFERENCES.SHOW_SURVEY, true)
  }

  set showSurvey(showSurvey) {
    this.setBoolean(PREFERENCES.SHOW_SURVEY, showSurvey)
  }

  get showSocialMediaSurvey() {
    return this.getBoolean(PREFERENCES.SHOW_SOCIAL_MEDIA_SURVEY, true)
  }

  set showSocialMediaSurvey(showSocialMediaSurvey) {
    this.setBoolean(PREFERENCES.SHOW_SOCIAL_MEDIA_SURVEY, showSocialMediaSurvey)
  }

  get showAnnualUpsellBanner() {
    return this.getBoolean(PREFERENCES.SHOW_ANNUAL_UPSELL_BANNER, true)
  }

  set showAnnualUpsellBanner(showAnnualUpsellBanner) {
    this.setBoolean(
      PREFERENCES.SHOW_ANNUAL_UPSELL_BANNER,
      showAnnualUpsellBanner
    )
  }

  get showTimezoneBanner() {
    return this.getBoolean(PREFERENCES.SHOW_TIMEZONE_BANNER, true)
  }

  set showTimezoneBanner(showTimezoneBanner) {
    this.setBoolean(PREFERENCES.SHOW_TIMEZONE_BANNER, showTimezoneBanner)
  }

  get showUpgradeBanner() {
    return this.getBoolean(PREFERENCES.SHOW_UPGRADE_BANNER, true)
  }

  set showUpgradeBanner(showUpgradeBanner) {
    this.setBoolean(PREFERENCES.SHOW_UPGRADE_BANNER, showUpgradeBanner)
  }

  get showTwitterBanner() {
    return this.getBoolean(PREFERENCES.SHOW_TWITTER_BANNER, true)
  }

  set showTwitterBanner(showTwitterBanner) {
    this.setBoolean(PREFERENCES.SHOW_TWITTER_BANNER, showTwitterBanner)
  }

  get showSubredditSettingsTooltip() {
    return this.getBoolean(PREFERENCES.SHOW_SUBREDDIT_SETTINGS, true)
  }

  set showSubredditSettingsTooltip(showSubredditSettingsTooltip) {
    this.setBoolean(
      PREFERENCES.SHOW_SUBREDDIT_SETTINGS,
      showSubredditSettingsTooltip
    )
  }

  get previewImages() {
    return this.getBoolean(PREFERENCES.PREVIEW_IMAGES_KEY, true)
  }

  set previewImages(previewImages) {
    this.setBoolean(PREFERENCES.PREVIEW_IMAGES_KEY, previewImages)
  }

  get showDashboardChecklist() {
    return this.getBoolean(PREFERENCES.SHOW_DASHBOARD_CHECKLIST, true)
  }

  set showDashboardChecklist(showDashboardChecklist) {
    this.setBoolean(
      PREFERENCES.SHOW_DASHBOARD_CHECKLIST,
      showDashboardChecklist
    )
  }

  get defaultPerPage() {
    return this.getNumber(PREFERENCES.DEFAULT_PER_PAGE, 10)
  }

  set defaultPerPage(defaultPerPage) {
    this.setNumber(PREFERENCES.DEFAULT_PER_PAGE, defaultPerPage)
  }

  get lastUsedInboxUsername() {
    return this.getString(PREFERENCES.LAST_USED_INBOX_USERNAME, '')
  }

  set lastUsedInboxUsername(lastUsedInboxUsername) {
    this.setString(PREFERENCES.LAST_USED_INBOX_USERNAME, lastUsedInboxUsername)
  }

  get lastUsedCalendarView() {
    return this.getString(PREFERENCES.LAST_USED_CALENDAR_VIEW, '')
  }

  set lastUsedCalendarView(lastUsedCalendarView) {
    this.setString(PREFERENCES.LAST_USED_CALENDAR_VIEW, lastUsedCalendarView)
  }

  get repostSettings() {
    return this.getObject(PREFERENCES.REPOST_SETTINGS, null)
  }

  set repostSettings(repostSettings) {
    this.setObject(PREFERENCES.REPOST_SETTINGS, repostSettings)
  }

  get lastReadChangelog() {
    return this.getDate(PREFERENCES.LAST_READ_CHANGELOG, null)
  }

  trackReadChangelog() {
    this.setDate(PREFERENCES.LAST_READ_CHANGELOG, new Date())
  }
}

export function getLocalPreferences() {
  const localPreferences = {}
  Object.values(PREFERENCES).forEach((key) => {
    localPreferences[key] = localStorage.getItem(key)
  })
  return localPreferences
}
